import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowRight, Check, Upload, Brain, FileCheck, BarChart3, Users, Clock, Sparkles, Briefcase, User, Building2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { ChatWidget } from '../components/PLGComponents';

const hFont = { fontFamily: 'Manrope, sans-serif' };

const USE_CASES = {
  'solo-founders': {
    icon: User,
    title: 'Built for Solo Founders & Side Hustlers',
    subtitle: 'Your finances, handled — so you can focus on building.',
    description: 'You\'re wearing every hat already. Bookkeeping shouldn\'t be one of them. Upload your bank statement, let the AI do the rest, and get back to what actually makes you money.',
    pain_points: [
      'Spending your evenings categorising expenses instead of working on your craft',
      'No idea if you\'re actually profitable until tax season hits',
      'Receipts piling up in a shoebox (or a random phone folder)',
      'Paying an accountant hundreds for what\'s basically data entry',
    ],
    how_kaya_helps: [
      { title: 'Drop your bank statement', desc: 'PDF, CSV, or snap a photo of a receipt. Done in seconds.' },
      { title: 'AI handles the boring stuff', desc: 'Every transaction classified, categorised, and split for tax — automatically.' },
      { title: 'Know your numbers instantly', desc: 'P&L, expense breakdown, tax-ready reports. No accounting degree needed.' },
    ],
    features: [
      'Works on your phone — upload from anywhere',
      'Automatic VAT/tax splitting',
      'AI that learns your spending patterns',
      'Free plan — no credit card needed',
      'Expense tracking by category',
      'Reports your accountant will love',
    ],
    cta: 'Start free — takes 30 seconds',
    testimonial: { text: 'I used to spend my entire Sunday on bookkeeping. Now I drop my statement into Kaya during my morning coffee and it\'s done before the cup is empty.', author: 'Independent Consultant, Cape Town' },
  },
  'startups': {
    icon: Sparkles,
    title: 'Built for Startups & Scale-ups',
    subtitle: 'Move fast. Stay compliant. Know your burn.',
    description: 'When you\'re moving at startup speed, you can\'t afford to wait weeks for financial reports. Kaya gives you real-time visibility into your numbers — from day one.',
    pain_points: [
      'Burning runway but no clear picture of where the money goes',
      'Financial reports that arrive weeks after month-end',
      'Outgrowing spreadsheets but not ready for enterprise accounting',
      'Investors asking for numbers you don\'t have ready',
    ],
    how_kaya_helps: [
      { title: 'Upload from all your accounts', desc: 'Multi-bank, multi-currency. Process everything in one place.' },
      { title: 'Real-time financial intelligence', desc: 'The moment you post, your P&L, Balance Sheet, and Cash Flow update instantly.' },
      { title: 'Investor-ready reports on demand', desc: 'Pull clean financials whenever you need them. No waiting for your accountant.' },
    ],
    features: [
      'Multi-seat team access',
      'Real-time P&L and Cash Flow',
      'Retained earnings continuity checks',
      'Multi-currency support',
      'Scale from free to enterprise',
      'Double-entry journal entries, automated',
    ],
    cta: 'Get started — it\'s free',
    testimonial: { text: 'Our investors asked for updated financials on a Friday afternoon. With Kaya, I sent them a clean P&L in 10 minutes. Try doing that with spreadsheets.', author: 'Co-founder, SaaS Startup' },
  },
  'growing-teams': {
    icon: Building2,
    title: 'Built for Growing Teams & SMEs',
    subtitle: 'Professional-grade accounting without the professional-grade price tag.',
    description: 'Your business has outgrown basic bookkeeping. But hiring a full-time finance team? Not yet. Kaya bridges the gap — giving you enterprise capabilities at a fraction of the cost.',
    pain_points: [
      'Multiple team members, no unified view of finances',
      'Accounting costs that scale with transaction volume',
      'Manual processes that break as the business grows',
      'No real-time visibility into cash position across entities',
    ],
    how_kaya_helps: [
      { title: 'One platform for the whole team', desc: 'Upload statements from any account. Everyone sees the same numbers.' },
      { title: 'AI does the heavy lifting', desc: 'Transaction classification, tax splitting, journal entries — all automated.' },
      { title: 'Scale without scaling costs', desc: 'Whether you process 50 or 5,000 transactions, the per-seat price stays the same.' },
    ],
    features: [
      'Unlimited team seats (paid plans)',
      'Up to 10 organisations under one roof',
      '7 financial reports, always current',
      'Approval workflows with editing',
      'Data integrity checks built in',
      'Per-seat pricing from $9.99/mo',
    ],
    cta: 'Try Kaya free for your team',
    testimonial: { text: 'We replaced 3 hours of weekly bookkeeping with a 5-minute upload. The AI gets smarter every month. Honestly, it\'s like having a junior accountant that never sleeps.', author: 'Operations Director, Logistics Company' },
  },
  'finance-pros': {
    icon: Briefcase,
    title: 'Built for Finance Professionals',
    subtitle: '10x your client capacity without hiring.',
    description: 'Your clients send you bank statements. You send them back clean books. Kaya handles the data processing so you can focus on advisory — the work that actually commands premium fees.',
    pain_points: [
      'Junior staff spending 80% of their time on data entry',
      'Human error in transaction classification costing rework',
      'Clients expecting faster turnaround than your team can deliver',
      'Scaling the practice means hiring — expensive and slow',
    ],
    how_kaya_helps: [
      { title: 'Clients upload, Kaya processes', desc: 'Bank statements auto-classified with AI-suggested accounts and tax rules.' },
      { title: 'You review, edit, and approve', desc: 'Two-step workflow gives you full control. Edit anything before posting.' },
      { title: 'Post to GL with confidence', desc: 'Retained earnings checks ensure data integrity across fiscal years.' },
    ],
    features: [
      'Multi-org management (up to 10 clients)',
      'Approval workflow with inline editing',
      'AI that learns from your corrections',
      'Retained earnings continuity checks',
      'Bulk transaction processing',
      'Export-ready financial reports',
    ],
    cta: 'Start your free trial',
    testimonial: { text: 'Kaya cut our data entry time by 70%. My team now focuses on advisory work — which is what clients actually pay premium rates for.', author: 'Practice Manager, Accounting Firm' },
  },
};

export default function UseCasePage() {
  const { persona } = useParams();
  const data = USE_CASES[persona];

  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-zinc-900">Page not found</h1>
          <Link to="/" className="text-[#D94E18] hover:underline mt-4 block">Back to home</Link>
        </div>
      </div>
    );
  }

  const Icon = data.icon;

  return (
    <div className="min-h-screen bg-[#FAFAF9]" data-testid={`usecase-${persona}`}>
      {/* Nav */}
      <nav className="sticky top-0 z-40 bg-white/90 backdrop-blur-md border-b border-zinc-100">
        <div className="max-w-5xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="text-xl font-black text-zinc-900" style={hFont}>kaya<span className="text-[#D94E18]">.ai</span></Link>
          <Link to="/login">
            <Button size="sm" className="bg-[#D94E18] hover:bg-[#C2410C] text-white">{data.cta}</Button>
          </Link>
        </div>
      </nav>

      {/* Hero */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div className="w-16 h-16 bg-[#D94E18]/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
            <Icon className="w-8 h-8 text-[#D94E18]" />
          </div>
          <h1 className="text-4xl sm:text-5xl font-extrabold text-zinc-900 tracking-tight" style={hFont}>
            {data.title}
          </h1>
          <p className="text-xl text-zinc-500 mt-4 max-w-2xl mx-auto">{data.subtitle}</p>
          <p className="text-base text-zinc-600 mt-4 max-w-xl mx-auto">{data.description}</p>
          <div className="mt-8">
            <Link to="/login">
              <Button size="lg" className="bg-[#D94E18] hover:bg-[#C2410C] text-white text-base px-8" data-testid="usecase-hero-cta">
                {data.cta} <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Pain Points */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-2xl font-bold text-zinc-900 mb-8 text-center" style={hFont}>Sound familiar?</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            {data.pain_points.map((p, idx) => (
              <div key={p} className="flex items-start gap-3 p-4 bg-white rounded-xl border border-zinc-200">
                <span className="w-6 h-6 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 text-red-500 text-xs font-bold">{idx + 1}</span>
                <p className="text-sm text-zinc-700">{p}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How Kaya Helps */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-2xl font-bold text-zinc-900 mb-10 text-center" style={hFont}>How Kaya helps</h2>
          <div className="space-y-8">
            {data.how_kaya_helps.map((step, idx) => (
              <div key={step.title} className="flex items-start gap-5">
                <div className="w-10 h-10 bg-[#D94E18] text-white rounded-xl flex items-center justify-center flex-shrink-0 text-sm font-bold">{idx + 1}</div>
                <div>
                  <h3 className="text-base font-semibold text-zinc-900">{step.title}</h3>
                  <p className="text-sm text-zinc-500 mt-1">{step.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-2xl font-bold text-zinc-900 mb-8 text-center" style={hFont}>What you get</h2>
          <div className="grid sm:grid-cols-2 gap-3 max-w-lg mx-auto">
            {data.features.map((f) => (
              <div key={f} className="flex items-center gap-2">
                <Check className="w-4 h-4 text-[#D94E18] flex-shrink-0" />
                <span className="text-sm text-zinc-700">{f}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonial */}
      <section className="py-16 bg-zinc-900">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <p className="text-xl text-white leading-relaxed italic">"{data.testimonial.text}"</p>
          <p className="text-sm text-zinc-400 mt-4">— {data.testimonial.author}</p>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-white text-center">
        <div className="max-w-2xl mx-auto px-6">
          <h2 className="text-3xl font-extrabold text-zinc-900" style={hFont}>Ready to get started?</h2>
          <p className="text-zinc-500 mt-3">Join hundreds of {persona === 'finance-pros' ? 'finance professionals' : persona === 'growing-teams' ? 'growing teams' : persona === 'startups' ? 'startups' : 'founders'} who trust Kaya with their numbers.</p>
          <div className="mt-8">
            <Link to="/login">
              <Button size="lg" className="bg-[#D94E18] hover:bg-[#C2410C] text-white text-base px-8" data-testid="usecase-bottom-cta">
                {data.cta} <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 text-center border-t border-zinc-100">
        <Link to="/" className="text-sm text-zinc-500 hover:text-[#D94E18]">kaya.ai</Link>
      </footer>

      <ChatWidget />
    </div>
  );
}
